import { useCallback } from 'react'
import { useParams } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { useQuery } from '@apollo/client/react/hooks/useQuery'
import { Box, Divider, Link, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarList, F0TagDot, F0TagRaw } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Desktop, Person, Suitcase } from '@factorialco/f0-react/icons/app'
import {
  EventSidepanelNavigationDocument,
  TrainingsSessionAttendanceStatusEnum
} from 'generated/resources/graphql'
import {
  AttendanceSessionEventSidepanelFragment,
  AttendanceSessionSidepanelDocument,
  AttendanceSidepanelFilterTeamNamesDocument,
  UpdateAttendanceEventSidepanelDocument
} from 'generated/resources/graphql'
import moment from 'moment'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useTrainingsDatatable } from 'modules/trainings/hooks/useTrainingsDatatable'
import { DEFAULT_DATA_COLLECTION_STORAGE } from 'modules/trainings/lib/collections'

interface BulkUpdateParams {
  ids: number[]
  status: TrainingsSessionAttendanceStatusEnum
  all?: boolean
  sessionId?: number
  search?: string
  teamIds?: number[]
  statusFilter?: string[]
}

const AttendanceSidepanel = () => {
  const { eventId, classId } = useParams()
  const client = useApolloClient()
  const [bulkUpdateTrainingsSessionAttendance] = useMutation(
    UpdateAttendanceEventSidepanelDocument,
    {
      extractorFn: (data) => data.trainingsMutations.bulkUpdateTrainingsSessionAttendance,
      refetchQueries: [AttendanceSessionSidepanelDocument]
    }
  )

  const { getSessionAttendanceTranslation, getDataCollectionSessionAttendanceColor } =
    useTrainingsDatatable()

  const source = useGraphqlDataCollectionSource(
    {
      document: AttendanceSessionSidepanelDocument,
      mapData: (data) => data.trainings.sessionAccessMembershipsConnection.nodes,
      search: {
        enabled: true,
        sync: true
      },
      mapVariables: ({ filters, sortings, search }) => {
        const teamIds = filters.team?.map((id) => id)
        const status = filters.status?.map((id) => id)
        const statusFilter = status?.map((item) => {
          if (item === 'pending') return 'not started'
          else if (item === 'inprogress') return 'started'
          else if (item === 'missing') return 'absent'
          else return 'completed'
        })
        track(trackingEvents.training.trainingSession.applyFilterAttendanceStatus, {
          selected_statusfilter: statusFilter
        })
        return {
          teamIds: teamIds?.length ? teamIds : undefined,
          status: status?.length ? status : undefined,
          sort: sortings?.[0],
          search: search,
          sessionId: Number(eventId)
        }
      },
      mapPagination: (data) => {
        return {
          total: data.trainings.sessionAccessMembershipsConnection.totalCount ?? 0
        }
      },
      sortings: {
        firstName: {
          label: i18n.t('trainings.trainings_revamp.event_sidepanel.sortings.name')
        },
        status: {
          label: i18n.t('trainings.trainings_revamp.event_sidepanel.sortings.status')
        }
      },
      defaultSortings: {
        field: 'firstName',
        order: 'asc'
      },
      filters: {
        team: {
          type: 'in',
          label: i18n.t('trainings.trainings_revamp.event_sidepanel.filters.team'),
          options: {
            options: async () => {
              const { data } = await client.query({
                query: AttendanceSidepanelFilterTeamNamesDocument,
                fetchPolicy: 'cache-first'
              })
              return data.teams.teamsConnection.nodes.map((team) => ({
                label: team.name,
                value: team.id
              }))
            }
          }
        },
        status: {
          type: 'in',
          label: i18n.t('trainings.trainings_revamp.event_sidepanel.filters.status'),
          options: {
            options: [
              {
                label: i18n.t('trainings.trainings_revamp.event_sidepanel.status.not_started'),
                value: 'pending'
              },
              {
                label: i18n.t('trainings.trainings_revamp.event_sidepanel.status.started'),
                value: 'inprogress'
              },
              {
                label: i18n.t('trainings.trainings_revamp.event_sidepanel.status.absent'),
                value: 'missing'
              },
              {
                label: i18n.t('trainings.trainings_revamp.event_sidepanel.status.completed'),
                value: 'completed'
              }
            ]
          }
        }
      },
      selectable: (item) => item.sessionAttendance?.id,
      bulkActions: (selectedItems) => {
        track(trackingEvents.training.trainingSession.selectParticipant, {
          selected_type: selectedItems?.allSelected ? 'all' : 'individual'
        })
        return {
          primary: [
            {
              id: 'update-attendance-completed',
              label: i18n.t('trainings.trainings_revamp.event_sidepanel.bulk_actions.completed')
            },
            {
              id: 'update-attendance-in-progress',
              label: i18n.t('trainings.trainings_revamp.event_sidepanel.bulk_actions.started')
            },
            {
              id: 'update-attendance-pending',
              label: i18n.t('trainings.trainings_revamp.event_sidepanel.bulk_actions.not_started')
            },
            {
              id: 'update-attendance-missing',
              label: i18n.t('trainings.trainings_revamp.event_sidepanel.bulk_actions.absent')
            }
          ]
        }
      }
    },
    [eventId]
  )

  const handleBulkAction = useCallback(
    async (
      actionId: string,
      selection: {
        allSelected: boolean | 'indeterminate'
        itemsStatus: ReadonlyArray<{
          item: AttendanceSessionEventSidepanelFragment
          checked: boolean
        }>
        selectedCount: number
      }
    ) => {
      const statusMap: Record<string, TrainingsSessionAttendanceStatusEnum> = {
        'update-attendance-completed': 'completed',
        'update-attendance-in-progress': 'inprogress',
        'update-attendance-pending': 'pending',
        'update-attendance-missing': 'missing'
      }
      const status = statusMap[actionId]
      if (!status) return

      let params: BulkUpdateParams
      const isSelectAll =
        selection.allSelected === true || selection.allSelected === 'indeterminate'

      if (isSelectAll) {
        // Get current filters and search from source
        const currentFilters = source.currentFilters
        const search = source.currentSearch
        const teamIds = currentFilters?.team
        const statusFilter = currentFilters?.status

        // Get excluded IDs (unchecked items when in indeterminate mode)
        const excludedIds =
          selection.allSelected === 'indeterminate'
            ? selection.itemsStatus
                .filter((item) => !item.checked)
                .map((item) => item.item.sessionAttendance?.id)
                .filter((id): id is number => id !== undefined)
            : []

        params = {
          status,
          all: true,
          sessionId: Number(eventId),
          search,
          teamIds,
          statusFilter,
          ids: excludedIds // Pass excluded IDs (empty array if none)
        }
      } else {
        // Manual selection - get selected IDs from itemsStatus
        const selectedIds = selection.itemsStatus
          .filter((item) => item.checked)
          .map((item) => item.item.sessionAttendance?.id)
          .filter((id): id is number => id !== undefined)

        if (selectedIds.length === 0) return

        params = {
          status,
          ids: selectedIds
        }
      }

      await bulkUpdateTrainingsSessionAttendance({
        variables: params
      })
    },
    [eventId, source.currentFilters, source.currentSearch, bulkUpdateTrainingsSessionAttendance]
  )

  const { getModalityTranslation } = useTrainingsDatatable()
  const { data } = useQuery(EventSidepanelNavigationDocument, {
    variables: { trainingClassIds: [String(classId)] }
  })
  const currentEvent = data?.trainings.sessionsConnection.nodes.find(
    (session) => session.id === Number(eventId)
  )

  const formatDuration = (duration: string) => {
    const totalMinutes = parseFloat(duration) * 60
    const hours = Math.floor(totalMinutes / 60)
    const minutes = Math.round(totalMinutes % 60)
    return `${hours}h ${minutes}m`
  }

  const getDate = (date?: string | null, asUtc = false) => {
    if (!date) return null

    const parsedDate = asUtc ? moment.utc(date) : moment(date)
    return i18nDateToString(parsedDate, 'ui_dmy_long')
  }

  const getTime = (dateString: string) => {
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }
    const time = new Date(dateString).toLocaleTimeString(undefined, timeOptions)
    return time
  }
  const duration = currentEvent?.duration ? formatDuration(currentEvent?.duration) : '-'

  const instructors =
    currentEvent?.sessionInstructors && currentEvent?.sessionInstructors?.length
      ? currentEvent?.sessionInstructors.map((instructor) => ({
          firstName: instructor.employee.firstName,
          lastName: instructor.employee.lastName,
          type: 'person' as const,
          src: buildUrl(instructor.employee?.avatar?.url ?? '')
        }))
      : []
  return (
    <Box gap={'s24'} flexDirection="column">
      <Box width="full" flexDirection="column" gap="s16">
        <Box width="full" flexDirection="row" gap="s12" justifyContent="spaceBetween">
          <Box flexDirection="column" gap="s6" width="s240">
            <Text size="200">
              {i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.type_label')}
            </Text>
            <Box width="fit-content">
              {currentEvent?.schedule === 'selfpaced' ? (
                <F0TagDot
                  color="malibu"
                  text={i18n.t(
                    'trainings.trainings_revamp.event_sidepanel.header_details.self-paced'
                  )}
                />
              ) : (
                <F0TagDot
                  color="barbie"
                  text={i18n.t(
                    'trainings.trainings_revamp.event_sidepanel.header_details.scheduled'
                  )}
                />
              )}
            </Box>
          </Box>
          <Box flexDirection="column" gap="s6" width="s240">
            <Text size="200">
              {i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.modality_label')}
            </Text>
            <Box width="fit-content">
              <F0TagRaw
                icon={
                  currentEvent?.modality === 'inperson'
                    ? Person
                    : currentEvent?.modality === 'online'
                    ? Desktop
                    : Suitcase
                }
                text={getModalityTranslation(currentEvent?.modality) || '-'}
              />
            </Box>
          </Box>
        </Box>
        <Box width="full" flexDirection="row" gap="s12" justifyContent="spaceBetween">
          <Box flexDirection="column" gap="s6" width="s240">
            <Text size="200">
              {i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.date_label')}
            </Text>
            {currentEvent?.schedule == 'selfpaced' ? (
              <Text size="200" weight="medium" color="grey1000">
                {getDate(currentEvent?.startsAt, true) ?? ''} -{' '}
                {getDate(currentEvent?.dueDate, true) ?? ''}
              </Text>
            ) : (
              <Text size="200" weight="medium" color="grey1000">
                {getDate(currentEvent?.startsAt) ?? '-'}
              </Text>
            )}
          </Box>
          <Box flexDirection="column" gap="s6" width="s240">
            <Text size="200">
              {i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.hour_label')}
            </Text>
            <Text size="200" weight="medium" color="grey1000">
              {currentEvent?.schedule == 'selfpaced'
                ? duration
                : i18n.t(
                    'trainings.trainings_revamp.event_sidepanel.header_details.session_time_frame',
                    {
                      starts: getTime(currentEvent?.startsAt),
                      ends: getTime(currentEvent?.endsAt),
                      duration: duration
                    }
                  )}
            </Text>
          </Box>
        </Box>
        <Box width="full" flexDirection="row" gap="s12" justifyContent="spaceBetween">
          <Box flexDirection="column" gap="s6" width="s240">
            <Text size="200">
              {i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.location_label')}
            </Text>
            <Text size="200" weight="medium" color="grey1000">
              {currentEvent?.location ? currentEvent?.location : '-'}
            </Text>
          </Box>
          <Box flexDirection="column" gap="s6" width="s240">
            {currentEvent?.schedule == 'scheduled' && instructors.length > 0 ? (
              <>
                <Text size="200">
                  {i18n.t(
                    'trainings.trainings_revamp.event_sidepanel.header_details.instructors_label'
                  )}
                </Text>
                <F0AvatarList avatars={instructors} size="sm" type="person" max={3} />
              </>
            ) : (
              <>
                <Text size="200">
                  {i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.link_label')}
                </Text>
                {currentEvent?.link ? (
                  <Link.Href href={currentEvent?.link} external title="Link">
                    <Text size="200" color="textLink">
                      {'Link'}
                    </Text>
                  </Link.Href>
                ) : (
                  '-'
                )}
              </>
            )}
          </Box>
        </Box>
        {currentEvent?.schedule == 'scheduled' && instructors.length > 0 && (
          <Box width="full" flexDirection="row" gap="s12" justifyContent="spaceBetween">
            <Box flexDirection="column" gap="s6" width="s240">
              <Text size="200">
                {i18n.t('trainings.trainings_revamp.event_sidepanel.header_details.link_label')}
              </Text>
              {currentEvent?.link ? (
                <Link.Href href={currentEvent?.link} external title="Link">
                  <Text size="200" color="textLink">
                    {'Link'}
                  </Text>
                </Link.Href>
              ) : (
                '-'
              )}
            </Box>
          </Box>
        )}
      </Box>
      <Divider />
      <OneDataCollection
        id="trainings/attendance/v1"
        storage={DEFAULT_DATA_COLLECTION_STORAGE}
        source={source}
        onBulkAction={handleBulkAction}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('trainings.trainings_revamp.event_sidepanel.table_columns.name'),
                  sorting: 'firstName',
                  render: (item) => ({
                    type: 'person',
                    value: {
                      src: buildUrl(item.access?.avatar ?? ''),
                      firstName: item.firstName ?? '',
                      lastName: item.lastName ?? ''
                    }
                  })
                },
                {
                  label: i18n.t(
                    'trainings.trainings_revamp.event_sidepanel.table_columns.attendance'
                  ),
                  render: (item) => ({
                    type: 'status',
                    value: {
                      label: getSessionAttendanceTranslation(item.sessionAttendance?.status),
                      status: getDataCollectionSessionAttendanceColor(
                        item.sessionAttendance?.status
                      )
                    }
                  })
                }
              ]
            }
          }
        ]}
      />
    </Box>
  )
}

export default AttendanceSidepanel
